import { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import * as Yup from 'yup';
import { Theme } from '@app/theme';
import { Select, Checkbox, SubmitButton, ErrorMsg } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { Formik, Form, FormikHelpers } from 'formik';
import { selectCurrentPanel } from 'store';
import { YupValidators } from 'lib/validators';
import { DataFormatGuide } from './DataFormatGuide';
import { clientSpecialConfigs } from 'config/clientSpecialConfigs';
import { useSelector } from 'react-redux';
import { CsvMapping, useParticipantFields } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  headerlLine: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.marginBase * 2,
    paddingRight: 0,
  },
  line: {
    extend: 'headerlLine',

    backgroundColor: theme.colors.grayBackground,
    marginTop: theme.marginBase,
  },
  titleCell: {
    ...theme.fonts.label,
    flex: 1,
    marginRight: theme.marginBase * 2,
  },
  cell: {
    extend: 'titleCell',
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  exampleCell: {
    extend: 'cell',
    overflow: 'hidden',
  },
  example: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectCell: {
    extend: 'cell',
    minWidth: 200,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

type Values = { mapping: CsvMapping; haveConsent: boolean };

interface Props {
  firstLines: string[][];
  onSubmit: (values: Values, helpers: FormikHelpers<Values>) => any;
  panelName: string;
}

export const ParticipantCsvMapping = ({
  panelName,
  firstLines,
  onSubmit,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        mapping: Yup.object().test(
          'haveMappedEmail',
          t('participants.list.importCsv.mapping.noEmailError'),
          (mapping: CsvMapping) => {
            return Object.values(mapping).includes('email');
          },
        ),
        haveConsent: YupValidators.isChecked(
          t('participants.list.importCsv.mapping.noConsentError'),
        ),
      }),
    [t],
  );

  const { data: participantFieldsData } = useParticipantFields();

  const participantFields = useMemo(
    () =>
      participantFieldsData
        ?.filter((f) => !!f.csv)
        .map((f) => ({
          value: f.name,
          columnAliases: f.csv?.columnAliases,
          guide: f.csv?.guide,
          label: f.label,
        })) || [],
    [participantFieldsData],
  );

  const firstLine = firstLines.length ? firstLines[0] : [];
  const otherLines = firstLines.length ? firstLines.slice(1) : [];

  const panel = useSelector(selectCurrentPanel);

  const initialValues: Values = {
    mapping: firstLine.reduce((mapping, key, index) => {
      const fieldInfo = participantFields.find(
        ({ value, columnAliases }) =>
          columnAliases?.includes(key.toLowerCase()) ||
          value.toLowerCase() === key.toLowerCase(),
      );
      if (!fieldInfo) {
        return mapping;
      }
      return {
        ...mapping,
        [index]: fieldInfo.value,
      };
    }, {} as CsvMapping),
    haveConsent: false,
  };

  const availableFields = clientSpecialConfigs.reduce((acc, config) => {
    const matchingConfig = config.orgId === (panel && panel.organizationId);
    if (!matchingConfig) {
      return acc;
    }
    return [
      ...acc,
      ...config.specialFields.map((field) => ({
        value: field.name,
        label: field.clientLabel,
        columnAliases: [],
      })),
    ];
  }, participantFields as any);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <div role="grid">
          <div className={classes.headerlLine}>
            <div className={classes.titleCell} role="columnheader">
              <Trans i18nKey="participants.list.importCsv.mapping.csvHeader" />
            </div>
            <div className={classes.titleCell} role="columnheader">
              <Trans i18nKey="participants.list.importCsv.mapping.csvData" />
            </div>
            <div className={classes.titleCell} role="columnheader">
              <Trans i18nKey="participants.list.importCsv.mapping.csvMapping" />
            </div>
          </div>
          {firstLine.map((cell, cellIndex) => (
            <div key={cellIndex} className={classes.line} role="row">
              <div role="rowheader" className={classes.titleCell}>
                {cell}
              </div>
              <div role="cell" className={classes.exampleCell}>
                {otherLines.map((line, lineIndex) => (
                  <div key={lineIndex} className={classes.example}>
                    {line[cellIndex]}
                  </div>
                ))}
              </div>
              <div role="cell" className={classes.selectCell}>
                <Select
                  name={`mapping[${cellIndex}]`}
                  options={availableFields}
                  isClearable
                  noMargin
                >
                  {(option?: { guide?: string }) => (
                    <DataFormatGuide text={option && t(option.guide)} />
                  )}
                </Select>
              </div>
            </div>
          ))}
        </div>
        <ErrorMsg name="mapping" />

        <Checkbox
          label={t('participants.list.importCsv.mapping.consent')}
          name="haveConsent"
        />

        <div className={classes.buttonContainer}>
          <SubmitButton
            color="green"
            text={
              panelName
                ? t('participants.list.importCsv.mapping.submit', {
                    name: panelName,
                  })
                : t('participants.list.importCsv.mapping.submitOnePanel', {
                    name: panelName,
                  })
            }
          />
        </div>
      </Form>
    </Formik>
  );
};
